/**
 * Utilitaires de couleur pour l'adaptation automatique du texte et du logo
 * selon la luminosité du fond sélectionné.
 */

/** Convertit une couleur hex en RGB. */
function hexToRgb(hex: string): { r: number; g: number; b: number } | null {
    const cleaned = hex.replace('#', '');
    if (cleaned.length === 3) {
        return {
            r: parseInt(cleaned[0] + cleaned[0], 16),
            g: parseInt(cleaned[1] + cleaned[1], 16),
            b: parseInt(cleaned[2] + cleaned[2], 16),
        };
    }
    if (cleaned.length === 6) {
        return {
            r: parseInt(cleaned.substring(0, 2), 16),
            g: parseInt(cleaned.substring(2, 4), 16),
            b: parseInt(cleaned.substring(4, 6), 16),
        };
    }
    return null;
}

/** Luminance relative selon la norme WCAG. */
function relativeLuminance(r: number, g: number, b: number): number {
    const toLinear = (c: number) => {
        const cs = c / 255;
        return cs <= 0.03928 ? cs / 12.92 : Math.pow((cs + 0.055) / 1.055, 2.4);
    };
    return 0.2126 * toLinear(r) + 0.7152 * toLinear(g) + 0.0722 * toLinear(b);
}

/** Calcule la luminance moyenne d'un dégradé entre deux couleurs. */
export function gradientLuminance(color1: string, color2: string): number {
    const c1 = hexToRgb(color1);
    const c2 = hexToRgb(color2);
    if (!c1 || !c2) return 0.5;
    const lum1 = relativeLuminance(c1.r, c1.g, c1.b);
    const lum2 = relativeLuminance(c2.r, c2.g, c2.b);
    return (lum1 + lum2) / 2;
}

/** Détermine si le fond est clair (true) ou sombre (false). */
export function isLightBackground(color1: string, color2: string): boolean {
    return gradientLuminance(color1, color2) > 0.45;
}

/** Retourne la couleur de texte optimale (noir ou blanc) selon le fond. */
export function getAdaptiveTextColor(color1: string, color2: string): string {
    return isLightBackground(color1, color2) ? '#1A1D27' : '#FFFFFF';
}

/** Retourne la couleur de texte secondaire (semi-transparent) selon le fond. */
export function getAdaptiveMutedColor(color1: string, color2: string): string {
    return isLightBackground(color1, color2) ? 'rgba(26, 29, 39, 0.7)' : 'rgba(255, 255, 255, 0.8)';
}

/** Retourne le style de fond pour les cartes semi-transparentes selon le fond. */
export function getAdaptiveCardBg(color1: string, color2: string): string {
    return isLightBackground(color1, color2)
        ? 'rgba(255, 255, 255, 0.55)'
        : 'rgba(255, 255, 255, 0.10)';
}

/** Retourne la couleur de bordure pour les cartes selon le fond. */
export function getAdaptiveBorderColor(color1: string, color2: string): string {
    return isLightBackground(color1, color2)
        ? 'rgba(26, 29, 39, 0.12)'
        : 'rgba(255, 255, 255, 0.15)';
}

/** Retourne le style de fond pour le logo selon le fond (pour rester visible). */
export function getAdaptiveLogoBg(color1: string, color2: string): string {
    return isLightBackground(color1, color2)
        ? 'rgba(255, 255, 255, 0.85)'
        : 'rgba(255, 255, 255, 0.12)';
}

/**
 * Extrait la couleur dominante d'une image (dataURL) via canvas.
 * Moyenne des pixels pondérée par saturation — les gris comptent moins.
 */
export function getDominantColor(src: string): Promise<string | null> {
    return new Promise((resolve) => {
        const img = new Image();
        img.onload = () => {
            try {
                const size = 32;
                const canvas = document.createElement('canvas');
                canvas.width = size;
                canvas.height = size;
                const ctx = canvas.getContext('2d');
                if (!ctx) { resolve(null); return; }
                ctx.drawImage(img, 0, 0, size, size);
                const { data } = ctx.getImageData(0, 0, size, size);
                let r = 0, g = 0, b = 0, w = 0;
                for (let i = 0; i < data.length; i += 4) {
                    const a = data[i + 3];
                    if (a < 128) continue;
                    const max = Math.max(data[i], data[i + 1], data[i + 2]);
                    const min = Math.min(data[i], data[i + 1], data[i + 2]);
                    const weight = 0.4 + ((max - min) / 255); // pixels saturés pèsent plus
                    r += data[i] * weight;
                    g += data[i + 1] * weight;
                    b += data[i + 2] * weight;
                    w += weight;
                }
                if (!w) { resolve(null); return; }
                const toHex = (v: number) => Math.round(v / w).toString(16).padStart(2, '0');
                resolve(`#${toHex(r)}${toHex(g)}${toHex(b)}`);
            } catch {
                resolve(null);
            }
        };
        img.onerror = () => resolve(null);
        img.src = src;
    });
}

/** Assombrit (p < 0) ou éclaircit (p > 0) une couleur hex de p%. */
export function shadeHex(hex: string, percent: number): string {
    const c = hexToRgb(hex);
    if (!c) return hex;
    const target = percent < 0 ? 0 : 255;
    const f = (v: number) => Math.round(v + (target - v) * Math.abs(percent) / 100);
    const toHex = (v: number) => f(v).toString(16).padStart(2, '0');
    return `#${toHex(c.r)}${toHex(c.g)}${toHex(c.b)}`;
}

/** Fait tourner la teinte d'une couleur hex de `deg` degrés (saturation garantie pour rester distincte). */
export function rotateHue(hex: string, deg: number): string {
    const c = hexToRgb(hex);
    if (!c) return hex;
    const r = c.r / 255, g = c.g / 255, b = c.b / 255;
    const max = Math.max(r, g, b), min = Math.min(r, g, b), d = max - min;
    let h = 0;
    const l = (max + min) / 2;
    let s = d === 0 ? 0 : d / (1 - Math.abs(2 * l - 1));
    if (d !== 0) {
        if (max === r) h = ((g - b) / d) % 6;
        else if (max === g) h = (b - r) / d + 2;
        else h = (r - g) / d + 4;
        h *= 60;
    }
    h = (((h + deg) % 360) + 360) % 360;
    s = Math.max(s, 0.55);
    const L = Math.min(0.6, Math.max(0.4, l));
    const C = (1 - Math.abs(2 * L - 1)) * s, X = C * (1 - Math.abs(((h / 60) % 2) - 1)), m = L - C / 2;
    const [r1, g1, b1] = h < 60 ? [C, X, 0] : h < 120 ? [X, C, 0] : h < 180 ? [0, C, X]
        : h < 240 ? [0, X, C] : h < 300 ? [X, 0, C] : [C, 0, X];
    const toHex = (v: number) => Math.round((v + m) * 255).toString(16).padStart(2, '0');
    return `#${toHex(r1)}${toHex(g1)}${toHex(b1)}`;
}
